import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import {
    Plus,
    X,
    Eye,
    Edit2,
    Search,
    Upload,
    Download,
    Briefcase,
    Ban,
} from "lucide-react";
import "./DemandesRecrutement.css";

import {
    fetchDemandesRecrutementRequest,
    createDemandeRecrutementRequest,
    updateDemandeRecrutementRequest,
    cancelDemandeRecrutementRequest,
} from "../../../../redux/actions/demandesRecrutementActions";

import { fetchCampagnesRequest } from "../../../../redux/actions/campagnesActions";
import { fetchEtablissementsRequest } from "../../../../redux/actions/etablissementsActions";
import { getAllJobsRequest } from "../../../../redux/actions/Jobactions";
import { API_BASE_URL, getBlobRequest } from "../../../../../api";

const NIVEAUX_ETUDES = [
    "Diplôme (Bac + 2)",
    "Licence (Bac + 3)",
    "Master (Bac + 5)",
    "Ingénieur (Bac + 5)",
    "Doctorat et plus",
];

const ANNEES_EXPERIENCE = [
    "< 1 an",
    "> 1 an",
    "> 2 ans",
    "> 5 ans",
    "> 10 ans",
];

const TYPES_EMPLOI = ["CDI", "CDD", "Intérim", "Freelance", "Autre"];
const TYPES_DEMANDE = ["Remplacement", "Nouveau poste"];

const STATUT_EN_ATTENTE = "En attente";
const STATUT_APPROUVEE = "Approuvée";
const STATUT_REJETEE = "Rejetée";
const STATUT_ANNULEE = "Annulée";
const STATUT_EN_COURS = "En cours";

const STATUTS_COMPTABILISES = [STATUT_EN_ATTENTE, STATUT_APPROUVEE, STATUT_EN_COURS];

const showSwal = (options) => {
    Swal.fire({
        ...options,
        customClass: {
            ...(options.customClass || {}),
            container: "swal-above-modal",
        },
        didOpen: () => {
            const container = document.querySelector(".swal-above-modal");
            if (container) container.style.zIndex = "99999";
            if (options.didOpen) options.didOpen();
        },
    });
};

const getActiveCampagneTypeFromRole = (role) => {
    if (role?.moduleName?.includes("Recrutement")) return "RECRUTEMENT";
    if (role?.moduleName?.includes("Formation")) return "FORMATION";
    return "FORMATION";
};

const Modal = ({ open, title, children, footer, onClose, size = "md" }) => {
    if (!open) return null;
    const sizeClass = size === "lg" ? "drModal__panel--lg" : "";

    return (
        <div className="drModal" role="dialog" aria-modal="true" onMouseDown={onClose}>
            <div className={`drModal__panel ${sizeClass}`} onMouseDown={(e) => e.stopPropagation()}>
                <div className="drModal__header">
                    <div className="drModal__title">{title}</div>
                    <button className="drIconBtn" onClick={onClose} aria-label="Fermer">
                        <X size={18} />
                    </button>
                </div>
                <div className="drModal__body">{children}</div>
                {footer && <div className="drModal__footer">{footer}</div>}
            </div>
        </div>
    );
};

const emptyForm = () => ({
    campagneId: "",
    posteId: "",
    autrePoste: "",
    niveauEtude: "",
    anneesExperience: "",
    typeDemande: "",
    typeEmploi: "",
    etablissementId: "",
    autreEtablissement: "",
    description: "",
    descriptifPosteFile: null,
    descriptifPostePreview: "",
});

export default function DemandesRecrutement() {
    const dispatch = useDispatch();

    const selectedRole = useSelector((state) => state.role?.selectedRole);
    const activeTypeCampagne = useMemo(() => getActiveCampagneTypeFromRole(selectedRole), [selectedRole]);

    const currentUser = useSelector((state) => state?.auth?.user);

    const demandesState = useSelector((state) => state.demandesRecrutement || {});
    const demandes = demandesState.demandes || [];
    const demandesLoading = !!demandesState.loading;

    const campagnesState = useSelector((state) => state.campagnes || {});
    const campagnes = campagnesState.list || [];

    const etablissementsState = useSelector((state) => state.etablissements || {});
    const etablissements = etablissementsState.etablissements || [];

    const jobsState = useSelector((state) => state.jobs || {});
    const jobs = jobsState.jobs || [];

    const campagnesRecrutement = useMemo(() => {
        return (campagnes || []).filter((c) => {
            const type = String(c.typeCampagne || "FORMATION").toUpperCase();
            return type === String(activeTypeCampagne || "FORMATION").toUpperCase() && c.status === true;
        });
    }, [campagnes, activeTypeCampagne]);

    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showCancelModal, setShowCancelModal] = useState(false);

    const [selectedDemande, setSelectedDemande] = useState(null);
    const [cancelMotif, setCancelMotif] = useState("");
    const [form, setForm] = useState(emptyForm());

    const [filters, setFilters] = useState({
        campagne: "",
        poste: "",
        niveauEtude: "",
        anneeExperience: "",
        typeEmploi: "",
        ecole: "",
        statut: "",
    });

    useEffect(() => {
        dispatch(fetchDemandesRecrutementRequest());
        dispatch(fetchCampagnesRequest());
        dispatch(fetchEtablissementsRequest());
        dispatch(getAllJobsRequest());
    }, [dispatch]);

    useEffect(() => {
        const isAnyModalOpen = showAddModal || showEditModal || showViewModal || showCancelModal;
        document.body.style.overflow = isAnyModalOpen ? "hidden" : "unset";
        return () => {
            document.body.style.overflow = "unset";
        };
    }, [showAddModal, showEditModal, showViewModal, showCancelModal]);

    const resetForm = () => setForm(emptyForm());

    const getJobName = (id) => {
        const job = (jobs || []).find((j) => Number(j.id) === Number(id));
        return job ? (job.name || job.nom) : "—";
    };

    const getEtablissementName = (id) => {
        const et = (etablissements || []).find((e) => Number(e.id) === Number(id));
        return et ? (et.abbreviation || et.nom) : "—";
    };

    const isAutreLabel = (value) => {
        return String(value || "").trim().toLowerCase() === "autre";
    };

    const isAutrePosteSelected = useMemo(() => {
        const selectedJob = (jobs || []).find((j) => Number(j.id) === Number(form.posteId));
        const label = selectedJob ? (selectedJob.name || selectedJob.nom) : "";
        return isAutreLabel(label);
    }, [form.posteId, jobs]);

    const isAutreEtablissementSelected = useMemo(() => {
        const selectedEtablissement = (etablissements || []).find((e) => Number(e.id) === Number(form.etablissementId));
        const label = selectedEtablissement ? (selectedEtablissement.abbreviation || selectedEtablissement.nom) : "";
        return isAutreLabel(label);
    }, [form.etablissementId, etablissements]);

    const getDisplayedPosteName = (demande) => {
        if (demande?.autrePoste?.trim()) return demande.autrePoste.trim();
        return demande?.posteNom || getJobName(demande?.posteId);
    };

    const getDisplayedEtablissementName = (demande) => {
        if (demande?.autreEtablissement?.trim()) return demande.autreEtablissement.trim();
        return demande?.etablissementNom || getEtablissementName(demande?.etablissementId);
    };

    const getDemandeStatus = (demande) => {
        return demande?.statut || demande?.status || STATUT_EN_ATTENTE;
    };

    const getStatusBadgeClass = (status) => {
        switch (status) {
            case STATUT_EN_ATTENTE: return "dr-status-pending";
            case STATUT_APPROUVEE: return "dr-status-approved";
            case STATUT_EN_COURS: return "dr-status-progress";
            case STATUT_REJETEE: return "dr-status-rejected";
            case STATUT_ANNULEE: return "dr-status-cancelled";
            default: return "";
        }
    };

    const getSelectedCampagne = () => {
        return (campagnesRecrutement || []).find((c) => Number(c.id) === Number(form.campagneId));
    };

    const getCampagneUsage = (campagne) => {
        if (!campagne || !Array.isArray(campagne.recruitmentLimits) || campagne.recruitmentLimits.length === 0) {
            return null;
        }

        const limits = campagne.recruitmentLimits;

        const globalLimitObj = limits.find((l) =>
            String(l.typeLimite || "").toLowerCase().includes("global")
        ) || null;

        let globalUsage = null;
        if (globalLimitObj && Number(globalLimitObj.limite) > 0) {
            const limite = Number(globalLimitObj.limite);
            const utilise = (demandes || []).filter(
                (d) =>
                    Number(d.campagneId) === Number(campagne.id) &&
                    STATUTS_COMPTABILISES.includes(getDemandeStatus(d))
            ).length;
            globalUsage = {
                label: "Limite globale",
                limite,
                utilise,
                restantes: Math.max(0, limite - utilise),
                isBlocked: utilise >= limite,
            };
        }

        const entiteLimits = limits.filter((l) =>
            String(l.typeLimite || "").includes("Par entité")
        );

        const entiteLimitObj =
            currentUser?.entityId != null
                ? entiteLimits.find((l) =>
                Number(l.entiteId) === Number(currentUser?.entityId)
            ) || null
                : null;

        let entiteUsage = null;
        if (entiteLimitObj && Number(entiteLimitObj.limite) > 0) {
            const limite = Number(entiteLimitObj.limite);
            const utilise = (demandes || []).filter(
                (d) =>
                    Number(d.campagneId) === Number(campagne.id) &&
                    STATUTS_COMPTABILISES.includes(getDemandeStatus(d))
            ).length;
            entiteUsage = {
                label: `Limite par entité — ${entiteLimitObj.entiteNom || ""}`,
                limite,
                utilise,
                restantes: Math.max(0, limite - utilise),
                isBlocked: utilise >= limite,
            };
        }

        if (!globalUsage && !entiteUsage) return null;

        const isBlocked =
            (globalUsage?.isBlocked ?? false) || (entiteUsage?.isBlocked ?? false);

        return { globalUsage, entiteUsage, isBlocked };
    };

    const getBlockedMessage = (usage) => {
        if (!usage?.isBlocked) return null;
        if (usage.globalUsage?.isBlocked && usage.entiteUsage?.isBlocked) {
            return `La limite globale (${usage.globalUsage.utilise}/${usage.globalUsage.limite}) et la limite par entité (${usage.entiteUsage.utilise}/${usage.entiteUsage.limite}) sont atteintes.`;
        }
        if (usage.globalUsage?.isBlocked) {
            return `La limite globale de cette campagne est atteinte (${usage.globalUsage.utilise}/${usage.globalUsage.limite}).`;
        }
        if (usage.entiteUsage?.isBlocked) {
            return `La limite par entité de cette campagne est atteinte (${usage.entiteUsage.utilise}/${usage.entiteUsage.limite}).`;
        }
        return "La limite de cette campagne est atteinte.";
    };

    const selectedCampagne = getSelectedCampagne();
    const campagneUsage = getCampagneUsage(selectedCampagne);
    const isCampagneBlocked = !!campagneUsage?.isBlocked;
    const blockedMessage = getBlockedMessage(campagneUsage);

    const filteredDemandes = useMemo(() => {
        let result = [...demandes];

        if (filters.campagne) {
            result = result.filter((d) => Number(d.campagneId) === Number(filters.campagne));
        }
        if (filters.poste) {
            result = result.filter((d) =>
                String(getDisplayedPosteName(d) || "").toLowerCase().includes(filters.poste.toLowerCase())
            );
        }
        if (filters.niveauEtude) {
            result = result.filter((d) => d.niveauEtude === filters.niveauEtude);
        }
        if (filters.anneeExperience) {
            result = result.filter((d) => d.anneesExperience === filters.anneeExperience);
        }
        if (filters.typeEmploi) {
            result = result.filter((d) => d.typeEmploi === filters.typeEmploi);
        }
        if (filters.ecole) {
            result = result.filter((d) => Number(d.etablissementId) === Number(filters.ecole));
        }
        if (filters.statut) {
            result = result.filter((d) => getDemandeStatus(d) === filters.statut);
        }

        return result;
    }, [demandes, filters, jobs, etablissements]);

    const isAnyFilterFilled = Object.values(filters).some((val) => val !== "");

    const handleFilterChange = (field, value) => {
        setFilters((prev) => ({ ...prev, [field]: value }));
    };

    const resetFilters = () => {
        setFilters({
            campagne: "",
            poste: "",
            niveauEtude: "",
            anneeExperience: "",
            typeEmploi: "",
            ecole: "",
            statut: "",
        });
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setForm((prev) => ({
                ...prev,
                descriptifPosteFile: file,
                descriptifPostePreview: file.name,
            }));
        }
    };

    const openCreate = () => {
        const defaultCampagneId = filters.campagne
            ? filters.campagne
            : (campagnesRecrutement[0]?.id ?? "");

        const defaultCampagne = (campagnesRecrutement || []).find(
            (c) => Number(c.id) === Number(defaultCampagneId)
        );
        const usage = getCampagneUsage(defaultCampagne);
        if (usage?.isBlocked) {
            showSwal({
                icon: "warning",
                text: getBlockedMessage(usage),
                timer: 2500,
                showConfirmButton: false,
            });
            return;
        }

        setForm({ ...emptyForm(), campagneId: defaultCampagneId ? String(defaultCampagneId) : "" });
        setShowAddModal(true);
    };

    const closeCreate = () => {
        setShowAddModal(false);
        resetForm();
    };

    const openEdit = (demande) => {
        setSelectedDemande(demande);
        setForm({
            campagneId: demande.campagneId || "",
            posteId: demande.posteId || "",
            autrePoste: demande.autrePoste || "",
            niveauEtude: demande.niveauEtude || "",
            anneesExperience: demande.anneesExperience || "",
            typeDemande: demande.typeDemande || "",
            typeEmploi: demande.typeEmploi || "",
            etablissementId: demande.etablissementId || "",
            autreEtablissement: demande.autreEtablissement || "",
            description: demande.description || "",
            descriptifPosteFile: null,
            descriptifPostePreview: "",
        });
        setShowEditModal(true);
    };

    const closeEdit = () => {
        setShowEditModal(false);
        setSelectedDemande(null);
        resetForm();
    };

    const openView = (demande) => {
        setSelectedDemande(demande);
        setShowViewModal(true);
    };

    const closeView = () => {
        setShowViewModal(false);
        setSelectedDemande(null);
    };

    const openCancel = (demande) => {
        setSelectedDemande(demande);
        setCancelMotif("");
        setShowCancelModal(true);
    };

    const closeCancel = () => {
        setShowCancelModal(false);
        setSelectedDemande(null);
        setCancelMotif("");
    };

    const isPending = (demande) => getDemandeStatus(demande) === STATUT_EN_ATTENTE;

    const validateForm = () => {
        if (!form.campagneId) {
            showSwal({ icon: "warning", text: "Campagne obligatoire", timer: 1800, showConfirmButton: false });
            return false;
        }
        if (!form.posteId) {
            showSwal({ icon: "warning", text: "Poste obligatoire", timer: 1800, showConfirmButton: false });
            return false;
        }
        if (isAutrePosteSelected && !form.autrePoste?.trim()) {
            showSwal({ icon: "warning", text: "Le champ autre poste est obligatoire", timer: 1800, showConfirmButton: false });
            return false;
        }
        if (!form.niveauEtude) {
            showSwal({ icon: "warning", text: "Niveau d'étude obligatoire", timer: 1800, showConfirmButton: false });
            return false;
        }
        if (!form.anneesExperience) {
            showSwal({ icon: "warning", text: "Années d'expérience obligatoires", timer: 1800, showConfirmButton: false });
            return false;
        }
        if (!form.typeDemande) {
            showSwal({ icon: "warning", text: "Type de demande obligatoire", timer: 1800, showConfirmButton: false });
            return false;
        }
        if (!form.typeEmploi) {
            showSwal({ icon: "warning", text: "Type d'emploi obligatoire", timer: 1800, showConfirmButton: false });
            return false;
        }
        if (!form.etablissementId) {
            showSwal({ icon: "warning", text: "Établissement obligatoire", timer: 1800, showConfirmButton: false });
            return false;
        }
        if (isAutreEtablissementSelected && !form.autreEtablissement?.trim()) {
            showSwal({ icon: "warning", text: "Le champ autre établissement est obligatoire", timer: 1800, showConfirmButton: false });
            return false;
        }
        if (campagneUsage?.isBlocked) {
            showSwal({
                icon: "warning",
                text: getBlockedMessage(campagneUsage),
                timer: 2500,
                showConfirmButton: false,
            });
            return false;
        }
        return true;
    };

    const submitCreate = () => {
        if (!validateForm()) return;

        dispatch(
            createDemandeRecrutementRequest({
                campagneId: Number(form.campagneId),
                posteId: Number(form.posteId),
                autrePoste: isAutrePosteSelected ? form.autrePoste : "",
                niveauEtude: form.niveauEtude,
                anneesExperience: form.anneesExperience,
                typeDemande: form.typeDemande,
                typeEmploi: form.typeEmploi,
                etablissementId: Number(form.etablissementId),
                autreEtablissement: isAutreEtablissementSelected ? form.autreEtablissement : "",
                description: form.description,
                descriptifPosteFile: form.descriptifPosteFile,
            })
        );
        closeCreate();
    };

    const submitEdit = () => {
        if (!validateForm()) return;
        if (!selectedDemande?.id) return;

        dispatch(
            updateDemandeRecrutementRequest(selectedDemande.id, {
                campagneId: Number(form.campagneId),
                posteId: Number(form.posteId),
                autrePoste: isAutrePosteSelected ? form.autrePoste : "",
                niveauEtude: form.niveauEtude,
                anneesExperience: form.anneesExperience,
                typeDemande: form.typeDemande,
                typeEmploi: form.typeEmploi,
                etablissementId: Number(form.etablissementId),
                autreEtablissement: isAutreEtablissementSelected ? form.autreEtablissement : "",
                description: form.description,
                descriptifPosteFile: form.descriptifPosteFile,
            })
        );
        closeEdit();
    };

    const confirmCancel = async () => {
        if (!selectedDemande?.id) return;
        if (!cancelMotif.trim()) {
            showSwal({
                icon: "warning",
                text: "Le motif d'annulation est obligatoire",
                timer: 1800,
                showConfirmButton: false,
            });
            return;
        }
        dispatch(cancelDemandeRecrutementRequest(selectedDemande.id, cancelMotif.trim()));
        closeCancel();
    };

    const downloadDescriptif = async (filename) => {
        try {
            if (!filename) return;

            const token = sessionStorage.getItem("token");
            const uid = sessionStorage.getItem("uid");
            const selectedCompanyId = sessionStorage.getItem("selectedCompanyId");

            if (!token || !uid) {
                showSwal({
                    icon: "warning",
                    text: "Session expirée. Veuillez vous reconnecter.",
                    timer: 1800,
                    showConfirmButton: false,
                });
                return;
            }

            const url = `${API_BASE_URL}/api/demandes-recrutement/descriptifs/${filename}`;

            const response = await getBlobRequest({
                url,
                token,
                extraHeaders: { uid, "X-Tenant-Id": selectedCompanyId },
            });

            const blob = new Blob([response.data]);
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = blobUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(blobUrl);
        } catch (error) {
            console.error("Erreur téléchargement descriptif:", error);
            showSwal({
                icon: "error",
                text: "Impossible de télécharger le descriptif du poste.",
                timer: 2200,
                showConfirmButton: false,
            });
        }
    };

    const BlockedAlert = ({ message }) => {
        if (!message) return null;
        return (
            <div className="drBlockedAlert">
                <Ban size={16} />
                <div>{message} Aucune nouvelle demande ne peut être ajoutée.</div>
            </div>
        );
    };

    const LimitBox = ({ usage }) => {
        if (!usage) return null;
        const { globalUsage, entiteUsage } = usage;

        return (
            <div className="drLimitBox">
                {globalUsage && (
                    <div className="drLimitRow">
                        <div className="drLimitTitle">{globalUsage.label}</div>
                        <div className={`drLimitValue ${globalUsage.isBlocked ? "drLimitValue--blocked" : ""}`}>
                            {globalUsage.utilise} / {globalUsage.limite}
                        </div>
                        <div className="drLimitSubtext">
                            {globalUsage.isBlocked
                                ? "Limite globale atteinte — aucune nouvelle demande possible."
                                : `Demandes restantes : ${globalUsage.restantes}`}
                        </div>
                    </div>
                )}
                {globalUsage && entiteUsage && <div className="drLimitDivider" />}
                {entiteUsage && (
                    <div className="drLimitRow">
                        <div className="drLimitTitle">{entiteUsage.label}</div>
                        <div className={`drLimitValue ${entiteUsage.isBlocked ? "drLimitValue--blocked" : ""}`}>
                            {entiteUsage.utilise} / {entiteUsage.limite}
                        </div>
                        <div className="drLimitSubtext">
                            {entiteUsage.isBlocked
                                ? "Limite par entité atteinte — aucune nouvelle demande possible."
                                : `Demandes restantes : ${entiteUsage.restantes}`}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="drPage">
            <div className="drHeader">
                <div>
                    <h1>Demandes de Recrutement</h1>
                    <p>Gérez vos demandes de recrutement</p>
                </div>
            </div>

            {/* Filtres */}
            <div className="drWrap">
                <div className="drCard">
                    <div className="drBody">
                        <div className="drFilterSection">
                            <div className="drFilterTitle">
                                <Search size={16} />
                                Filtre de recherche
                            </div>

                            <div className="drFilterGrid">
                                <div className="drField">
                                    <label>Campagne</label>
                                    <select value={filters.campagne} onChange={(e) => handleFilterChange("campagne", e.target.value)}>
                                        <option value="">Toutes les campagnes</option>
                                        {campagnesRecrutement.map((c) => (
                                            <option key={c.id} value={c.id}>{c.nom}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="drField">
                                    <label>Intitulé du poste</label>
                                    <input
                                        type="text"
                                        value={filters.poste}
                                        onChange={(e) => handleFilterChange("poste", e.target.value)}
                                        placeholder="Rechercher par poste..."
                                    />
                                </div>

                                <div className="drField">
                                    <label>Niveau d'étude</label>
                                    <select value={filters.niveauEtude} onChange={(e) => handleFilterChange("niveauEtude", e.target.value)}>
                                        <option value="">Tous</option>
                                        {NIVEAUX_ETUDES.map((n) => (
                                            <option key={n} value={n}>{n}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="drField">
                                    <label>Établissement</label>
                                    <select value={filters.ecole} onChange={(e) => handleFilterChange("ecole", e.target.value)}>
                                        <option value="">Tous</option>
                                        {etablissements.map((e) => (
                                            <option key={e.id} value={e.id}>{e.abbreviation || e.nom}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="drField">
                                    <label>Années d'expérience</label>
                                    <select value={filters.anneeExperience} onChange={(e) => handleFilterChange("anneeExperience", e.target.value)}>
                                        <option value="">Toutes</option>
                                        {ANNEES_EXPERIENCE.map((a) => (
                                            <option key={a} value={a}>{a}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="drField">
                                    <label>Type d'emploi</label>
                                    <select value={filters.typeEmploi} onChange={(e) => handleFilterChange("typeEmploi", e.target.value)}>
                                        <option value="">Tous</option>
                                        {TYPES_EMPLOI.map((t) => (
                                            <option key={t} value={t}>{t}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="drField">
                                    <label>Statut</label>
                                    <select value={filters.statut} onChange={(e) => handleFilterChange("statut", e.target.value)}>
                                        <option value="">Tous</option>
                                        <option value={STATUT_EN_ATTENTE}>{STATUT_EN_ATTENTE}</option>
                                        <option value={STATUT_APPROUVEE}>{STATUT_APPROUVEE}</option>
                                        <option value={STATUT_REJETEE}>{STATUT_REJETEE}</option>
                                        <option value={STATUT_ANNULEE}>{STATUT_ANNULEE}</option>
                                    </select>
                                </div>
                            </div>

                            <div className="drFilterActions">
                                <button className="drBtn drBtn--primary" type="button" onClick={openCreate}>
                                    <Plus size={16} />
                                    Ajouter une demande
                                </button>
                                <button
                                    className={`drBtn ${isAnyFilterFilled ? "drBtn--warning" : "drBtn--ghost"}`}
                                    type="button"
                                    onClick={resetFilters}
                                    disabled={!isAnyFilterFilled}
                                >
                                    Réinitialiser les filtres
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Liste */}
            <div className="drWrap">
                <div className="drCard">
                    <div className="drBody">
                        <div className="drTopRow">
                            <h2 className="drTitle">Demandes de recrutement ({filteredDemandes.length})</h2>
                        </div>

                        <div className="drTableWrap">
                            <table className="drTable">
                                <thead>
                                <tr>
                                    <th>Campagne</th>
                                    <th>Intitulé du poste</th>
                                    <th>Niveau d'étude</th>
                                    <th>Établissement</th>
                                    <th>Expérience</th>
                                    <th>Type emploi</th>
                                    <th>Statut</th>
                                    <th className="is-center">Actions</th>
                                </tr>
                                </thead>
                                <tbody>
                                {demandesLoading ? (
                                    <tr>
                                        <td colSpan={8} className="drEmptyCell">Chargement...</td>
                                    </tr>
                                ) : filteredDemandes.length === 0 ? (
                                    <tr>
                                        <td colSpan={8} className="drEmptyCell">Aucune demande de recrutement trouvée</td>
                                    </tr>
                                ) : (
                                    filteredDemandes.map((demande) => {
                                        const status = getDemandeStatus(demande);
                                        const cancellable = isPending(demande);

                                        return (
                                            <tr key={demande.id}>
                                                <td className="drMuted">{demande.campagneNom || "—"}</td>
                                                <td>
                                                    <div className="drInline">
                                                        <Briefcase size={14} className="drMutedIcon" />
                                                        <span className="drStrong">{getDisplayedPosteName(demande)}</span>
                                                    </div>
                                                </td>
                                                <td className="drMuted">{demande.niveauEtude || "—"}</td>
                                                <td className="drMuted">{getDisplayedEtablissementName(demande)}</td>
                                                <td className="drMuted">{demande.anneesExperience || "—"}</td>
                                                <td>
                                                    <span className="drBadge">{demande.typeEmploi || "—"}</span>
                                                </td>
                                                <td>
                                                    <span className={`drStatus ${getStatusBadgeClass(status)}`}>
                                                        {status}
                                                    </span>
                                                </td>
                                                <td className="is-right">
                                                    <div className="drRowActions">
                                                        <button className="drIconBtn" type="button" title="Voir" onClick={() => openView(demande)}>
                                                            <Eye size={16} />
                                                        </button>
                                                        <button className="drIconBtn" type="button" title="Modifier" onClick={() => openEdit(demande)}>
                                                            <Edit2 size={16} />
                                                        </button>
                                                        <button
                                                            className={`drIconBtn ${cancellable ? "drIconBtn--cancel" : "drIconBtn--disabled"}`}
                                                            type="button"
                                                            title={cancellable ? "Annuler la demande" : `Annulation impossible (${status})`}
                                                            onClick={cancellable ? () => openCancel(demande) : undefined}
                                                            disabled={!cancellable}
                                                        >
                                                            <Ban size={16} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            {/* ── ADD MODAL ── */}
            <Modal
                open={showAddModal}
                title="Nouvelle demande de recrutement"
                onClose={closeCreate}
                size="lg"
                footer={
                    <>
                        <button className="drBtn drBtn--ghost" type="button" onClick={closeCreate}>Annuler</button>
                        <button
                            className="drBtn drBtn--primary"
                            type="button"
                            onClick={submitCreate}
                            disabled={isCampagneBlocked}
                        >
                            Enregistrer
                        </button>
                    </>
                }
            >
                <div className={`drForm ${isCampagneBlocked ? "drForm--blocked" : ""}`}>
                    {isCampagneBlocked && <BlockedAlert message={blockedMessage} />}

                    <div className="drFormRow">
                        <div className="drField">
                            <label>Campagne *</label>
                            <select
                                value={form.campagneId}
                                onChange={(e) => setForm((p) => ({ ...p, campagneId: e.target.value }))}
                                disabled={isCampagneBlocked}
                            >
                                {/*<option value="">Sélectionner une campagne</option>*/}
                                {campagnesRecrutement.map((c) => (
                                    <option key={c.id} value={c.id}>{c.nom}</option>
                                ))}
                            </select>
                        </div>

                        <div className="drField">
                            <label>Poste *</label>
                            <select
                                value={form.posteId}
                                onChange={(e) =>
                                    setForm((p) => ({
                                        ...p,
                                        posteId: e.target.value,
                                        autrePoste: (() => {
                                            const selectedJob = (jobs || []).find((j) => Number(j.id) === Number(e.target.value));
                                            const label = selectedJob ? (selectedJob.name || selectedJob.nom) : "";
                                            return isAutreLabel(label) ? p.autrePoste : "";
                                        })(),
                                    }))
                                }
                                disabled={isCampagneBlocked}
                            >
                                <option value="">Sélectionner un poste</option>
                                {jobs.map((j) => (
                                    <option key={j.id} value={j.id}>{j.name || j.nom}</option>
                                ))}
                            </select>
                        </div>

                        <div className="drField">
                            <label>Autre poste</label>
                            <input
                                type="text"
                                value={form.autrePoste}
                                onChange={(e) => setForm((p) => ({ ...p, autrePoste: e.target.value }))}
                                placeholder="Saisir l'autre poste"
                                disabled={!isAutrePosteSelected || isCampagneBlocked}
                            />
                        </div>
                    </div>

                    {selectedCampagne && campagneUsage && (
                        <LimitBox usage={campagneUsage} />
                    )}

                    <div className="drFormRow">
                        <div className="drField">
                            <label>Niveau d'étude *</label>
                            <select
                                value={form.niveauEtude}
                                onChange={(e) => setForm((p) => ({ ...p, niveauEtude: e.target.value }))}
                                disabled={isCampagneBlocked}
                            >
                                <option value="">Sélectionner un niveau</option>
                                {NIVEAUX_ETUDES.map((n) => (
                                    <option key={n} value={n}>{n}</option>
                                ))}
                            </select>
                        </div>

                        <div className="drField">
                            <label>Années d'expérience *</label>
                            <select
                                value={form.anneesExperience}
                                onChange={(e) => setForm((p) => ({ ...p, anneesExperience: e.target.value }))}
                                disabled={isCampagneBlocked}
                            >
                                <option value="">Sélectionner</option>
                                {ANNEES_EXPERIENCE.map((a) => (
                                    <option key={a} value={a}>{a}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="drFormRow">
                        <div className="drField">
                            <label>Type de demande *</label>
                            <select
                                value={form.typeDemande}
                                onChange={(e) => setForm((p) => ({ ...p, typeDemande: e.target.value }))}
                                disabled={isCampagneBlocked}
                            >
                                <option value="">Sélectionner un type</option>
                                {TYPES_DEMANDE.map((t) => (
                                    <option key={t} value={t}>{t}</option>
                                ))}
                            </select>
                        </div>

                        <div className="drField">
                            <label>Type d'emploi *</label>
                            <select
                                value={form.typeEmploi}
                                onChange={(e) => setForm((p) => ({ ...p, typeEmploi: e.target.value }))}
                                disabled={isCampagneBlocked}
                            >
                                <option value="">Sélectionner un type</option>
                                {TYPES_EMPLOI.map((t) => (
                                    <option key={t} value={t}>{t}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="drFormRow">
                        <div className="drField">
                            <label>Établissement *</label>
                            <select
                                value={form.etablissementId}
                                onChange={(e) =>
                                    setForm((p) => ({
                                        ...p,
                                        etablissementId: e.target.value,
                                        autreEtablissement: (() => {
                                            const selectedEtablissement = (etablissements || []).find((et) => Number(et.id) === Number(e.target.value));
                                            const label = selectedEtablissement ? (selectedEtablissement.abbreviation || selectedEtablissement.nom) : "";
                                            return isAutreLabel(label) ? p.autreEtablissement : "";
                                        })(),
                                    }))
                                }
                                disabled={isCampagneBlocked}
                            >
                                <option value="">Sélectionner un établissement</option>
                                {etablissements.map((e) => (
                                    <option key={e.id} value={e.id}>{e.abbreviation || e.nom}</option>
                                ))}
                            </select>
                        </div>

                        <div className="drField">
                            <label>Autre établissement</label>
                            <input
                                type="text"
                                value={form.autreEtablissement}
                                onChange={(e) => setForm((p) => ({ ...p, autreEtablissement: e.target.value }))}
                                placeholder="Saisir l'autre établissement"
                                disabled={!isAutreEtablissementSelected || isCampagneBlocked}
                            />
                        </div>
                    </div>

                    <div className="drField">
                        <label>Description</label>
                        <textarea
                            rows={4}
                            value={form.description}
                            onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                            placeholder="Description du poste et des attentes..."
                            disabled={isCampagneBlocked}
                        />
                    </div>

                    <div className="drField">
                        <label>Descriptif du poste</label>
                        <div className="drFileUpload">
                            <input
                                type="file"
                                id="descriptif-upload"
                                accept=".pdf,.doc,.docx"
                                onChange={handleFileChange}
                                style={{ display: "none" }}
                                disabled={isCampagneBlocked}
                            />
                            <label
                                htmlFor={isCampagneBlocked ? undefined : "descriptif-upload"}
                                className={`drFileUploadBtn ${isCampagneBlocked ? "drFileUploadBtn--disabled" : ""}`}
                            >
                                <Upload size={16} />
                                Choisir un fichier
                            </label>
                            {form.descriptifPostePreview && <div className="drFileName">{form.descriptifPostePreview}</div>}
                        </div>
                    </div>
                </div>
            </Modal>

            {/* ── EDIT MODAL ── */}
            <Modal
                open={showEditModal}
                title={`Modifier la demande — ${getDisplayedPosteName(selectedDemande) || ""}`}
                onClose={closeEdit}
                size="lg"
                footer={
                    <>
                        <button className="drBtn drBtn--ghost" type="button" onClick={closeEdit}>Annuler</button>
                        <button className="drBtn drBtn--primary" type="button" onClick={submitEdit}>Enregistrer</button>
                    </>
                }
            >
                <div className="drForm">
                    <div className="drFormRow">
                        <div className="drField">
                            <label>Campagne *</label>
                            <select value={form.campagneId} onChange={(e) => setForm((p) => ({ ...p, campagneId: e.target.value }))}>
                                <option value="">Sélectionner une campagne</option>
                                {campagnesRecrutement.map((c) => (
                                    <option key={c.id} value={c.id}>{c.nom}</option>
                                ))}
                            </select>
                        </div>

                        <div className="drField">
                            <label>Poste *</label>
                            <select
                                value={form.posteId}
                                onChange={(e) =>
                                    setForm((p) => ({
                                        ...p,
                                        posteId: e.target.value,
                                        autrePoste: (() => {
                                            const selectedJob = (jobs || []).find((j) => Number(j.id) === Number(e.target.value));
                                            const label = selectedJob ? (selectedJob.name || selectedJob.nom) : "";
                                            return isAutreLabel(label) ? p.autrePoste : "";
                                        })(),
                                    }))
                                }
                            >
                                <option value="">Sélectionner un poste</option>
                                {jobs.map((j) => (
                                    <option key={j.id} value={j.id}>{j.name || j.nom}</option>
                                ))}
                            </select>
                        </div>

                        <div className="drField">
                            <label>Autre poste</label>
                            <input
                                type="text"
                                value={form.autrePoste}
                                onChange={(e) => setForm((p) => ({ ...p, autrePoste: e.target.value }))}
                                placeholder="Saisir l'autre poste"
                                disabled={!isAutrePosteSelected}
                            />
                        </div>
                    </div>

                    {selectedCampagne && campagneUsage && (
                        <LimitBox usage={campagneUsage} />
                    )}

                    <div className="drFormRow">
                        <div className="drField">
                            <label>Niveau d'étude *</label>
                            <select value={form.niveauEtude} onChange={(e) => setForm((p) => ({ ...p, niveauEtude: e.target.value }))}>
                                <option value="">Sélectionner un niveau</option>
                                {NIVEAUX_ETUDES.map((n) => (
                                    <option key={n} value={n}>{n}</option>
                                ))}
                            </select>
                        </div>

                        <div className="drField">
                            <label>Années d'expérience *</label>
                            <select value={form.anneesExperience} onChange={(e) => setForm((p) => ({ ...p, anneesExperience: e.target.value }))}>
                                <option value="">Sélectionner</option>
                                {ANNEES_EXPERIENCE.map((a) => (
                                    <option key={a} value={a}>{a}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="drFormRow">
                        <div className="drField">
                            <label>Type de demande *</label>
                            <select value={form.typeDemande} onChange={(e) => setForm((p) => ({ ...p, typeDemande: e.target.value }))}>
                                <option value="">Sélectionner un type</option>
                                {TYPES_DEMANDE.map((t) => (
                                    <option key={t} value={t}>{t}</option>
                                ))}
                            </select>
                        </div>

                        <div className="drField">
                            <label>Type d'emploi *</label>
                            <select value={form.typeEmploi} onChange={(e) => setForm((p) => ({ ...p, typeEmploi: e.target.value }))}>
                                <option value="">Sélectionner un type</option>
                                {TYPES_EMPLOI.map((t) => (
                                    <option key={t} value={t}>{t}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="drFormRow">
                        <div className="drField">
                            <label>Établissement *</label>
                            <select
                                value={form.etablissementId}
                                onChange={(e) =>
                                    setForm((p) => ({
                                        ...p,
                                        etablissementId: e.target.value,
                                        autreEtablissement: (() => {
                                            const selectedEtablissement = (etablissements || []).find((et) => Number(et.id) === Number(e.target.value));
                                            const label = selectedEtablissement ? (selectedEtablissement.abbreviation || selectedEtablissement.nom) : "";
                                            return isAutreLabel(label) ? p.autreEtablissement : "";
                                        })(),
                                    }))
                                }
                            >
                                <option value="">Sélectionner un établissement</option>
                                {etablissements.map((e) => (
                                    <option key={e.id} value={e.id}>{e.abbreviation || e.nom}</option>
                                ))}
                            </select>
                        </div>

                        <div className="drField">
                            <label>Autre établissement</label>
                            <input
                                type="text"
                                value={form.autreEtablissement}
                                onChange={(e) => setForm((p) => ({ ...p, autreEtablissement: e.target.value }))}
                                placeholder="Saisir l'autre établissement"
                                disabled={!isAutreEtablissementSelected}
                            />
                        </div>
                    </div>

                    <div className="drField">
                        <label>Description</label>
                        <textarea
                            rows={4}
                            value={form.description}
                            onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                            placeholder="Description du poste et des attentes..."
                        />
                    </div>

                    <div className="drField">
                        <label>Descriptif du poste</label>
                        {selectedDemande?.descriptifPoste && (
                            <div className="drExistingFile">
                                <Download size={14} />
                                <button
                                    type="button"
                                    className="drLink"
                                    onClick={() => downloadDescriptif(selectedDemande.descriptifPoste)}
                                    style={{ background: "none", border: "none", padding: 0, cursor: "pointer" }}
                                >
                                    {selectedDemande.descriptifPoste}
                                </button>
                            </div>
                        )}
                        <div className="drFileUpload">
                            <input
                                type="file"
                                id="descriptif-upload-edit"
                                accept=".pdf,.doc,.docx"
                                onChange={handleFileChange}
                                style={{ display: "none" }}
                            />
                            <label htmlFor="descriptif-upload-edit" className="drFileUploadBtn">
                                <Upload size={16} />
                                {selectedDemande?.descriptifPoste ? "Remplacer le fichier" : "Choisir un fichier"}
                            </label>
                            {form.descriptifPostePreview && <div className="drFileName">{form.descriptifPostePreview}</div>}
                        </div>
                    </div>
                </div>
            </Modal>

            {/* ── VIEW MODAL ── */}
            <Modal
                open={showViewModal}
                title={`Détails — ${getDisplayedPosteName(selectedDemande) || ""}`}
                onClose={closeView}
                footer={<button className="drBtn drBtn--primary" type="button" onClick={closeView}>Fermer</button>}
            >
                {selectedDemande && (
                    <div className="drViewContent">
                        <div className="drViewRow">
                            <div className="drViewLabel">Campagne :</div>
                            <div className="drViewValue">{selectedDemande.campagneNom || "—"}</div>
                        </div>
                        <div className="drViewRow">
                            <div className="drViewLabel">Intitulé du poste :</div>
                            <div className="drViewValue">{getDisplayedPosteName(selectedDemande)}</div>
                        </div>
                        <div className="drViewRow">
                            <div className="drViewLabel">Niveau d'études :</div>
                            <div className="drViewValue">{selectedDemande.niveauEtude || "—"}</div>
                        </div>
                        <div className="drViewRow">
                            <div className="drViewLabel">Établissement :</div>
                            <div className="drViewValue">{getDisplayedEtablissementName(selectedDemande)}</div>
                        </div>
                        <div className="drViewRow">
                            <div className="drViewLabel">Années d'expérience :</div>
                            <div className="drViewValue">{selectedDemande.anneesExperience || "—"}</div>
                        </div>
                        <div className="drViewRow">
                            <div className="drViewLabel">Type de demande :</div>
                            <div className="drViewValue">{selectedDemande.typeDemande || "—"}</div>
                        </div>
                        <div className="drViewRow">
                            <div className="drViewLabel">Type d'emploi :</div>
                            <div className="drViewValue">{selectedDemande.typeEmploi || "—"}</div>
                        </div>
                        <div className="drViewRow">
                            <div className="drViewLabel">Statut :</div>
                            <div className="drViewValue">
                                <span className={`drStatus ${getStatusBadgeClass(getDemandeStatus(selectedDemande))}`}>
                                    {getDemandeStatus(selectedDemande)}
                                </span>
                            </div>
                        </div>
                        <div className="drViewRow">
                            <div className="drViewLabel">Motif :</div>
                            <div className="drViewValue">{selectedDemande.motif || "—"}</div>
                        </div>
                        <div className="drViewRow">
                            <div className="drViewLabel">Description :</div>
                            <div className="drViewValue">{selectedDemande.description || "—"}</div>
                        </div>
                        {selectedDemande.descriptifPoste && (
                            <div className="drViewRow">
                                <div className="drViewLabel">Descriptif du poste :</div>
                                <div className="drViewValue">
                                    <button
                                        type="button"
                                        className="drLink"
                                        onClick={() => downloadDescriptif(selectedDemande.descriptifPoste)}
                                        style={{
                                            background: "none",
                                            border: "none",
                                            padding: 0,
                                            cursor: "pointer",
                                            display: "inline-flex",
                                            alignItems: "center",
                                            gap: "6px",
                                        }}
                                    >
                                        <Download size={14} />
                                        {selectedDemande.descriptifPoste}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </Modal>

            {/* ── CANCEL MODAL ── */}
            <Modal
                open={showCancelModal}
                title="Annuler la demande"
                onClose={closeCancel}
                footer={
                    <>
                        <button className="drBtn drBtn--ghost" type="button" onClick={closeCancel}>Retour</button>
                        <button className="drBtn drBtn--cancel" type="button" onClick={confirmCancel} disabled={!cancelMotif.trim()}>
                            <Ban size={14} />
                            Confirmer l'annulation
                        </button>
                    </>
                }
            >
                <div className="drField">
                    <label>Motif d'annulation *</label>
                    <textarea
                        rows={4}
                        value={cancelMotif}
                        onChange={(e) => setCancelMotif(e.target.value)}
                        placeholder="Veuillez préciser le motif d'annulation..."
                    />
                </div>
            </Modal>
        </div>
    );
}